import React from 'react';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { SummaryListPairsProps } from '../../components/SummaryList/SummaryList';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { SummaryList } from '../../components/SummaryList/SummaryList';
import { Button } from '../../controls/Button/Button';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Body } from '../../text/Body';
import { space } from '../../utilities/shared/sizes';
import {
  PreviewCard as PreviewCardRoot,
  PreviewCardContent,
  PreviewCardTrigger,
} from '../PreviewCard/PreviewCard';

const HoverCardIcon = styled(DynamicIcon, {
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },
});
HoverCardIcon.displayName = 'HoverCardIcon';

const HoverCardHeading = styled(Body, {
  fontWeight: '$bold',
});
HoverCardHeading.displayName = 'HoverCardHeading';

const HoverCardStart = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
});
HoverCardStart.displayName = 'HoverCardStart';

const HoverCardHeader = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '$8',
});
HoverCardHeader.displayName = 'HoverCardHeader';

/** Inner surface: matches previous Ariakit hovercard padding and column gap. */
const HoverCardSurface = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$12',
  padding: '$12 $16',
  minWidth: '$240',
  width: '100%',
});
HoverCardSurface.displayName = 'HoverCardSurface';

const HoverCardTriggerWrap = styled('div');
HoverCardTriggerWrap.displayName = 'HoverCardTriggerWrap';

export type HoverCardProps = {
  /**
   * How to align the card relative to the trigger, along the axis perpendicular to `side`.
   * Defaults to `'center'`.
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * The trigger for the hover card.
   */
  children: React.ReactNode;
  /**
   * Delay in ms before the card closes after the pointer leaves the trigger. Defaults to `600`.
   */
  closeDelay?: number;
  /**
   * The content of the hover card.
   */
  content?: React.ReactNode;
  /**
   * Delay in ms before the card opens after the pointer enters the trigger. Defaults to `100`.
   */
  delay?: number;
  /**
   * The heading of the hover card.
   */
  heading: React.ReactNode;
  /**
   * The icon of the hover card.
   */
  icon?: DynamicIconName;
  /**
   * The link to the object the hover card describes.
   */
  link?: {
    /**
     * The component to render the link as.
     */
    as?: React.ElementType;
    /**
     * The to of the link.
     */
    to?: any;
  };
  /**
   * Whether the hover card is open.
   */
  open?: boolean;
  /**
   * Which side of the trigger to display the card on. Defaults to `'bottom'`.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * The summary list pairs of the hover card.
   */
  summary?: SummaryListPairsProps[];
};

/**
 * Rich hover preview with heading, optional summary, and body — built on
 * `PreviewCard` from `@meterup/atto` (Base UI `@base-ui/react/preview-card`).
 */
export function HoverCard({
  alignment,
  children,
  closeDelay = 600,
  content,
  delay = 100,
  heading,
  icon,
  link,
  open,
  side,
  summary,
}: HoverCardProps) {
  return (
    <PreviewCardRoot {...(open !== undefined ? { open } : {})}>
      <PreviewCardTrigger
        closeDelay={closeDelay}
        delay={delay}
        render={<HoverCardTriggerWrap>{children}</HoverCardTriggerWrap>}
      />
      <PreviewCardContent
        alignment={alignment}
        collisionPadding={12}
        gutter={{ edge: 'none' }}
        side={side}
        sideOffset={8}
      >
        <HoverCardSurface>
          <HoverCardHeader>
            <HoverCardStart>
              {icon && <HoverCardIcon icon={icon} size={space(14)} />}
              {heading && <HoverCardHeading>{heading}</HoverCardHeading>}
            </HoverCardStart>
            {link && (
              <Button
                as={link.as}
                to={link.to}
                arrangement="hidden-label"
                icon="chevron-right"
                size="small"
                variant="secondary"
              >
                Go to {heading}
              </Button>
            )}
          </HoverCardHeader>
          {summary && <SummaryList pairs={summary} />}
          {content}
        </HoverCardSurface>
      </PreviewCardContent>
    </PreviewCardRoot>
  );
}
